// @flow

// Libs
import React from 'react'
import _find from 'lodash/find'
import _round from 'lodash/round'
import { createSelector } from 'reselect'
import { FormattedMessage } from 'react-intl'

// Utils
import { getCropUnit } from 'hfty-utils'

// Messages
import {
  messages,
  convertRecommendationToFrontendUnits,
} from 'hfty-recommendations'

// DataTable Composer
import composeDataTable from 'fc-component-datatable'

// Hocs
import RecommendationsDataTableHoc from './hocs/RecommendationsDataTable'

// Constants
import {
  PROP_ID,
  PROP_YIELD_TARGET,
  PROP_CONTROLLER_TYPE,
  PROP_REMOVAL_CROP_GROWN,
  PROP_REMOVAL_YIELD,
  PROP_RECOMMENDATION_TYPE,
  PROP_PREVIOUS_CROP,
  PROP_CREDIT_OTHER_PRESCRIPTIONS,
  PROP_STATUS,
  PROP_RXRATES,
  PROP_ASSET,
  PROP_CROP,
  PROP_REMOVAL_CROP,
  PROP_CONTROLLER,
  PROP_FERTILIZER,
  PROP_ELEMENT_PICK,
  RECOMMENDATION_FERTILIZER_SET,
  RECOMMENDATION_TYPES,
  COMPLETE,
} from 'hfty-model-recommendations'
import {
  PROP_CROP_ID,
  PROP_NAME as PROP_CROP_NAME,
} from 'fc-model-crops'
import {
  PROP_NAME,
  PROP_N,
  PROP_P,
  PROP_K,
  PROP_S,
  PROP_CA,
  PROP_LIME,
  PROP_CU,
  PROP_FE,
  PROP_B,
  PROP_ZN,
  PROP_MG,
  PROP_MN,
} from 'fc-model-fertilizers'
import {
  DATATABLE_NAME,
  ATTR_RECOMMENDATION_ID,
  ATTR_CROP,
  ATTR_YIELD_TARGET,
  ATTR_TARGET_PH,
  ATTR_RECOMMENDATION_TYPE,
  ATTR_CONTROLLER_TYPE,
  ATTR_ELEMENTS,
  ATTR_PRODUCTS,
  ATTR_STATUS,
  ATTR_CONTROLLER,
  ATTR_REMOVAL_CROP_GROWN,
  ATTR_REMOVAL_YIELD,
  ATTR_PREVIOUS_CROP,
  ATTR_CREDIT_OTHER_PRESCRIPTIONS,
  ATTR_RXRATES,
  ATTR_ASSET,
  ATTR_REMOVAL_CROP,
  ATTR_HAS_MAP_REPORTS,
  ATTR_HAS_CONTROLLER_FILES,
  DEFAULT_TARGET_PH,
} from './constants'

// Types
import type { Recommendation } from 'hfty-model-recommendations/types'
import type { RecommendationRecord } from '../types'
import type {
  DataTableState,
  DataTableModule,
} from 'fc-component-datatable/types'
import type { Crop } from 'fc-model-crops/types'
import type { Fertilizer } from 'fc-model-fertilizers/types'
type ComposeRecommendationsDataTableArgs = {
  recommendations$: (state: Object) => Array<Recommendation>,
  tableState$: (state: Object) => DataTableState,
}

// Exports
export default function composeRecommendationsDataTable ({
  recommendations$,
  tableState$,
  crops$,
  currentUserUnits$,
  fertilizers$,
  }: ComposeRecommendationsDataTableArgs): DataTableModule<Recommendation, RecommendationRecord> {
  //
  function makeRecords (records$: (state: Object) =>
    Array<Recommendation>): (state: Object) => Array<RecommendationRecord> {
    return createSelector(
      [ records$, crops$, currentUserUnits$, fertilizers$ ],
      function (
        recommendations: Array<Recommendation>,
        crops: Array<Crop>,
        currentUserUnits: string,
        fertilizers: Array<Fertilizer>,
      ): Array<RecommendationRecord> {
        console.warn('Recompute Records')

        return recommendations.map((recommendation: Recommendation): RecommendationRecord => {
          const converted_recommendation =
            convertRecommendationToFrontendUnits(recommendation, crops, currentUserUnits, fertilizers)
          const crop = _find(crops, {[PROP_CROP_ID]: converted_recommendation[PROP_CROP]}) || {}
          const removalCrop = _find(crops, {[PROP_CROP_ID]: converted_recommendation[PROP_REMOVAL_CROP]}) || {}
          const cropUnit = getCropUnit(crop, currentUserUnits)
          const removalCropUnit = getCropUnit(removalCrop, currentUserUnits)
          const removalYieldValue = converted_recommendation[PROP_REMOVAL_YIELD]
          const recommendationType: Object = _find(RECOMMENDATION_TYPES, {
            value: converted_recommendation[PROP_RECOMMENDATION_TYPE],
          }) || {}
          const targetYieldValue = _round(converted_recommendation[PROP_YIELD_TARGET], 2) ||
            converted_recommendation[PROP_YIELD_TARGET]
          const targetYieldUnit = messages[`AddRecommendation.${cropUnit}`].defaultMessage
          const yieldTarget = `${targetYieldValue} ${targetYieldUnit}`
          const isRecommendationCompleted = converted_recommendation[PROP_STATUS] === COMPLETE
          const fertilizerSet = converted_recommendation[RECOMMENDATION_FERTILIZER_SET]
          let fertilizersMap = {}
          let elements = []
          let products = []
          const elementsOrder = [PROP_N, PROP_P, PROP_K, PROP_S, PROP_CA, PROP_LIME, PROP_CU, PROP_FE,
            PROP_B, PROP_ZN, PROP_MG, PROP_MN]

          if (fertilizers) {
            fertilizersMap = fertilizerSet.map((fertilizer: Fertilizer): Object => {
              const fertilizerId = fertilizer[PROP_FERTILIZER]
              const fertilizerData = fertilizers[fertilizerId] || {}
              const productFullName = fertilizerData[PROP_NAME] || ''
              const productCommonName = productFullName.match(/\([\w,\s]+\)/gi)
              const product = productCommonName ? productCommonName[0].replace(/[(,)]/g, '') : productFullName

              return {
                element: fertilizer[PROP_ELEMENT_PICK],
                product,
              }
            })
          }

          if (fertilizersMap) {
            fertilizersMap.sort((cur: Object, next: Object): number => {
              const curName = cur.element ? cur.element.toLowerCase() : ''
              const nextName = next.element ? next.element.toLowerCase() : ''

              return elementsOrder.indexOf(curName) - elementsOrder.indexOf(nextName)
            })

            fertilizersMap.forEach((fertilizerMap: Object) => {
              elements.push(fertilizerMap.element)
              products.push(fertilizerMap.product)
            })
          }

          return {
            [ATTR_RECOMMENDATION_ID]: converted_recommendation[PROP_ID],
            [ATTR_CROP]: crop ? crop[PROP_CROP_NAME] : '...',
            [ATTR_YIELD_TARGET]: yieldTarget,
            [ATTR_TARGET_PH]: DEFAULT_TARGET_PH,
            [ATTR_RECOMMENDATION_TYPE]: recommendationType.label,
            [ATTR_CONTROLLER_TYPE]: converted_recommendation[PROP_CONTROLLER_TYPE],
            [ATTR_ELEMENTS]: elements.join(', '),
            [ATTR_PRODUCTS]: products.join(', '),
            [ATTR_STATUS]: converted_recommendation[PROP_STATUS],
            [ATTR_CONTROLLER]: converted_recommendation[PROP_CONTROLLER],
            [ATTR_REMOVAL_CROP_GROWN]: converted_recommendation[PROP_REMOVAL_CROP_GROWN],
            [ATTR_REMOVAL_YIELD]: (<span>
              <span style={{marginRight: 5}}>
                {_round(removalYieldValue, 2) || removalYieldValue}
              </span>
              <FormattedMessage {...messages[`AddRecommendation.${removalCropUnit}`]} />
            </span>),
            [ATTR_PREVIOUS_CROP]: converted_recommendation[PROP_PREVIOUS_CROP],
            [ATTR_CREDIT_OTHER_PRESCRIPTIONS]: converted_recommendation[PROP_CREDIT_OTHER_PRESCRIPTIONS],
            [ATTR_RXRATES]: converted_recommendation[PROP_RXRATES],
            [ATTR_ASSET]: converted_recommendation[PROP_ASSET],
            [ATTR_REMOVAL_CROP]: converted_recommendation[PROP_REMOVAL_CROP],
            [ATTR_HAS_MAP_REPORTS]: converted_recommendation[PROP_STATUS] === COMPLETE,
            [ATTR_HAS_CONTROLLER_FILES]: isRecommendationCompleted && !!converted_recommendation[PROP_CONTROLLER],
          }
        })
      }
    )
  }

  const datatable: DataTableModule<Recommendation, RecommendationRecord> = composeDataTable({name: DATATABLE_NAME})({
    data$: recommendations$,
    tableState$,
    makeRecords,
  })

  return {
    ...datatable,
    DataTable: RecommendationsDataTableHoc(datatable.DataTable),
  }
}
